/**
 * FormFieldConnector — Connector/integration field components.
 *
 * Handles: Polymarket (market input, outcome selector), asset selector, price condition,
 * price input, X402, Slack channel, Telegram chat, email (from/to), wallet selector,
 * workflow selector, turnkey wallet.
 */
import { formatLabel } from "@/utils/string";
import { PRICE_CONDITION_LABELS, TRIGGER_FREQUENCY_LABELS } from "@/types/aiBlocks";
import { SOLANA_MAINNET_CHAIN_ID } from "@/lib/chains";
import { isSolanaNodeType } from "@/lib/utils/chain-family";
import * as React from "react";
import AssetSelector from "./AssetSelector";
import EmailFromInput from "./EmailFromInput";
import EmailInput from "./EmailInput";
import GoogleSheetInput from "./GoogleSheetInput";
import PolymarketMarketInput from "./PolymarketMarketInput";
import PolymarketOutcomeSelector from "./PolymarketOutcomeSelector";
import PolymarketUserInput from "./PolymarketUserInput";
import PriceInput from "./PriceInput";
import SelectInput from "./SelectInput";
import QuickBooksAccountSelector from "./QuickBooksAccountSelector";
import DiscordChannelSelector from "./DiscordChannelSelector";
import SlackChannelSelector from "./SlackChannelSelector";
import SlackUserSelector from "./SlackUserSelector";
import ConnectorAccountSelector from "./ConnectorAccountSelector";
import PushbulletDeviceSelector from "./PushbulletDeviceSelector";
import PushoverRecipientSelector from "./PushoverRecipientSelector";
import WebPushSubscriptionSelector from "./WebPushSubscriptionSelector";
import TelegramChatSelector from "./TelegramChatSelector";
import WalletSelectorInput from "./WalletSelectorInput";
import WorkflowPayloadFields from "./WorkflowPayloadFields";
import WorkflowSelector from "./WorkflowSelector";
import X402ConfigInput from "./X402ConfigInput";
import type { FormFieldInternalProps } from "./FormField";

import { tokenAmountToString } from "@/lib/workflow/tokenAmount";

/**
 * Attempts to render a connector/integration field component.
 * Returns a React element if the field matches, or `null` to indicate
 * the caller should continue to the next category.
 */
export function renderConnectorField({
  name,
  property,
  value,
  onChange,
  handleChange,
  allValues,
  currentNodeId,
  currentNodeType,
  allNodes,
  allEdges,
  hideLabel,
  nodeDefinitions,
  connectorId,
  connectorType,
  withVariableToggle,
}: FormFieldInternalProps & {
  handleChange: (value: unknown) => void;
  connectorId: string | undefined;
  connectorType: string | undefined;
  withVariableToggle: (element: React.ReactElement) => React.ReactElement;
}): React.ReactElement | null {
  // Special case for polymarket-place-bet - use Polymarket-style betting UI
  // This component handles all bet configuration fields together
  if (property.type === "polymarket-market" || (name === "marketUrl" && currentNodeType === "polymarket-place-bet")) {
    // Build config from all polymarket-related fields
    // Define defaults - FAK for market orders (immediate fill), GTC for limit orders
    const defaults = {
      side: "BUY" as const,
      orderType: "FAK" as const, // FAK for market orders (default priceMode is "market")
      amountMode: "fixed" as const,
      fixedAmount: "1000000",
      priceMode: "market" as const,
      multiplier: 1,
      minAmount: 1,
      maxAmount: 1,
      limitPrice: 0.5,
      dynamicAmount: "",
      copyPriceVariable: "{{root.result.trade.price}}",
    };

    const polymarketConfig = {
      // Mode
      mode: allValues.mode || "manual",
      // Manual mode fields
      marketUrl: allValues.marketUrl || "",
      outcome: allValues.outcome || "",
      groupOutcome: allValues.groupOutcome || "",
      tokenId: allValues.tokenId || "",
      side: allValues.side || defaults.side,
      // Copy mode fields
      tokenIdVariable: allValues.tokenIdVariable || "",
      sideVariable: allValues.sideVariable || "",
      // Common fields
      orderType: allValues.orderType || defaults.orderType,
      amountMode: allValues.amountMode || defaults.amountMode,
      fixedAmount: tokenAmountToString(allValues.fixedAmount) ?? allValues.size ?? defaults.fixedAmount,
      dynamicAmount: allValues.dynamicAmount || defaults.dynamicAmount,
      multiplier: allValues.multiplier ?? defaults.multiplier,
      minAmount: allValues.minAmount ?? defaults.minAmount,
      maxAmount: allValues.maxAmount ?? defaults.maxAmount,
      priceMode: allValues.priceMode || defaults.priceMode,
      limitPrice: allValues.limitPrice ?? allValues.price ?? defaults.limitPrice,
      copyPriceVariable: allValues.copyPriceVariable || defaults.copyPriceVariable,
    };

    // Propagate defaults to form data if not already set
    // This ensures schema validation passes
    const missingDefaults: Record<string, string | number> = {};
    const isCopyMode = allValues.mode === "copy";
    if (isCopyMode) {
      // Copy mode: hardcode variable references so the workflow engine resolves them
      if (!allValues.tokenIdVariable) missingDefaults.tokenIdVariable = "{{root.result.trade.tokenId}}";
      if (!allValues.sideVariable) missingDefaults.sideVariable = "{{root.result.trade.side}}";
      if (!allValues.dynamicAmount) missingDefaults.dynamicAmount = "{{root.result.trade.size}}";
      if (!allValues.amountMode) missingDefaults.amountMode = "proportional";
    } else {
      // Manual mode defaults
      if (!allValues.side) missingDefaults.side = defaults.side;
      if (!allValues.amountMode) missingDefaults.amountMode = defaults.amountMode;
      if (!allValues.priceMode) missingDefaults.priceMode = defaults.priceMode;
      if (!allValues.orderType) missingDefaults.orderType = defaults.orderType;
      if (allValues.fixedAmount === undefined) missingDefaults.fixedAmount = defaults.fixedAmount;
      // Persist normalized value for legacy number fixedAmount
      if (typeof allValues.fixedAmount === "number") {
        missingDefaults.fixedAmount = tokenAmountToString(allValues.fixedAmount)!;
      }
    }

    // Call onChange for missing defaults (will be batched by React)
    if (Object.keys(missingDefaults).length > 0) {
      // Use setTimeout to avoid updating during render
      setTimeout(() => {
        Object.entries(missingDefaults).forEach(([key, val]) => {
          onChange(key, val);
        });
      }, 0);
    }

    return (
      <PolymarketMarketInput
        config={polymarketConfig}
        onChange={updates => {
          // Update all changed fields
          Object.entries(updates).forEach(([key, val]) => {
            onChange(key, val);
          });
          // Also map some fields to the schema fields
          if (updates.fixedAmount !== undefined && updates.amountMode === "fixed") {
            onChange("size", updates.fixedAmount);
          }
          if (updates.limitPrice !== undefined && updates.priceMode === "limit") {
            onChange("price", updates.limitPrice);
          }
        }}
        currentNodeId={currentNodeId}
        allNodes={allNodes}
        allEdges={allEdges}
        nodeDefinitions={nodeDefinitions}
      />
    );
  }

  // Skip rendering fields handled by PolymarketMarketInput
  if (currentNodeType === "polymarket-place-bet") {
    const polymarketHandledFields = [
      // Mode selection
      "mode",
      // Manual mode fields
      "outcome",
      "groupOutcome",
      "side",
      "tokenId",
      // Copy mode fields
      "tokenIdVariable",
      "sideVariable",
      // Common fields
      "orderType",
      "amountMode",
      "fixedAmount",
      "dynamicAmount",
      "multiplier",
      "minAmount",
      "maxAmount",
      "priceMode",
      "limitPrice",
      "copyPriceVariable",
      "price",
      "size",
      "expiresAt", // All handled by the unified Polymarket UI
    ];
    if (polymarketHandledFields.includes(name)) {
      // Return fragment to signal "handled" (renders nothing)
      return <></>;
    }
  }

  // Polymarket outcome selector for ASK blocks - lightweight URL + outcome picker
  if (property.type === "polymarket-outcome") {
    return (
      <PolymarketOutcomeSelector
        label={hideLabel ? "" : property.title || "Polymarket Market"}
        description={hideLabel ? "" : property.description}
        value={value || { market: "", outcome: "", tokenId: "" }}
        onChange={handleChange}
        placeholder={property.placeholder}
      />
    );
  }

  // Polymarket user input - username/address with validation and profile link
  if (property.format === "polymarket-user" || (name === "user" && currentNodeType === "polymarket-user-bet")) {
    return withVariableToggle(
      <PolymarketUserInput
        label={hideLabel ? "" : property.title || formatLabel(name)}
        description={hideLabel ? "" : property.description}
        value={value ?? ""}
        onChange={handleChange}
        required={property.required || false}
      />,
    );
  }

  // Special case for token-price-cexes asset field - use AssetSelector
  if (property.type === "asset-selector" || (name === "asset" && currentNodeType === "token-price-cexes")) {
    return withVariableToggle(
      <AssetSelector
        label={hideLabel ? "" : property.title || "Asset"}
        description={
          hideLabel ? "" : property.description || "Select the trading pair to monitor (e.g., BTCUSD, ETHUSD)"
        }
        value={value}
        onChange={handleChange}
        required={property.required || false}
      />,
    );
  }

  // Special case for price alert operation field - use SelectInput with friendly labels
  if (
    property.type === "price-condition" ||
    (name === "operation" && (currentNodeType === "token-price-cexes" || currentNodeType === "hyperliquid-price-alert"))
  ) {
    const options = property.enum || ["cross-up", "cross-down"];
    const labels = options.map((op: string) => PRICE_CONDITION_LABELS[op] || op);

    return withVariableToggle(
      <SelectInput
        label={hideLabel ? "" : property.title || "Condition"}
        description={hideLabel ? "" : property.description || "Price condition to trigger the workflow"}
        value={value ?? property.default ?? ""}
        options={options}
        optionLabels={labels}
        onChange={handleChange}
      />,
    );
  }

  // Trigger frequency selector for price alerts
  if (
    name === "maxTriggers" &&
    (currentNodeType === "token-price-cexes" || currentNodeType === "hyperliquid-price-alert")
  ) {
    const options = property.enum || ["once", "every-time"];
    const labels = options.map((op: string) => TRIGGER_FREQUENCY_LABELS[op] || op);

    return withVariableToggle(
      <SelectInput
        label={hideLabel ? "" : property.title || "Trigger frequency"}
        description={hideLabel ? "" : property.description}
        value={value ?? property.default ?? "once"}
        options={options}
        optionLabels={labels}
        onChange={handleChange}
      />,
    );
  }

  // Special case for token-price-cexes targetPrice field - use PriceInput
  if (property.type === "price" || (name === "targetPrice" && currentNodeType === "token-price-cexes")) {
    return withVariableToggle(
      <PriceInput
        label={hideLabel ? "" : property.title || "Target Price"}
        description={hideLabel ? "" : property.description || "Enter the target price in USD"}
        value={value}
        onChange={handleChange}
        required={property.required || false}
        currentNodeId={currentNodeId}
        allNodes={allNodes}
        allEdges={allEdges}
        nodeDefinitions={nodeDefinitions}
      />,
    );
  }

  // Special case for x402 config - use dedicated component
  if (property.type === "x402" || name === "x402") {
    return <X402ConfigInput value={value} onChange={handleChange} />;
  }

  // Slack channel selector (multi-select for array fields like trigger conversations)
  if (property.type === "array" && property.items?.format === "slack-channel") {
    return withVariableToggle(
      <SlackChannelSelector
        label={hideLabel ? "" : property.title || property.label || formatLabel(name)}
        description={hideLabel ? "" : property.description}
        value={value || []}
        onChange={handleChange}
        required={property.required || false}
        connectorId={connectorId}
        connectorType={connectorType}
        onResolvedChange={property.onResolvedChange}
        disabled={property.disabled || property.readOnly}
        multiSelect
      />,
    );
  }

  // Slack channel selector - searchable dropdown populated from Slack API
  if (property.type === "slack-channel" || property.format === "slack-channel") {
    return withVariableToggle(
      <SlackChannelSelector
        label={hideLabel ? "" : property.title || formatLabel(name)}
        description={hideLabel ? "" : property.description}
        value={value ?? ""}
        onChange={handleChange}
        required={property.required || false}
        connectorId={connectorId}
        connectorType={connectorType}
        onConnectorChange={property.onConnectorChange}
        onResolvedChange={property.onResolvedChange}
        disabled={property.disabled || property.readOnly}
      />,
    );
  }

  // Discord channel selector - searchable dropdown populated from Discord API
  if (property.type === "discord-channel" || property.format === "discord-channel") {
    return withVariableToggle(
      <DiscordChannelSelector
        label={hideLabel ? "" : property.title || formatLabel(name)}
        description={hideLabel ? "" : property.description}
        value={value ?? ""}
        onChange={handleChange}
        required={property.required || false}
        connectorId={connectorId}
        connectorType={connectorType}
        disabled={property.disabled || property.readOnly}
      />,
    );
  }

  // Slack user multi-selector — for actions that DM one or more users by ID.
  // Output is a comma-separated string to match the Slack action's `users`
  // payload shape ("U01ABC,U02XYZ").
  if (property.type === "slack-users" || property.format === "slack-users") {
    return withVariableToggle(
      <SlackUserSelector
        label={hideLabel ? "" : property.title || formatLabel(name)}
        description={hideLabel ? "" : property.description}
        value={typeof value === "string" ? value : ""}
        onChange={handleChange}
        required={property.required || false}
        connectorId={connectorId}
        connectorType={connectorType}
        disabled={property.disabled || property.readOnly}
      />,
    );
  }

  // QuickBooks account selector - searchable dropdown populated from QuickBooks API
  if (property.type === "quickbooks-account" || property.format === "quickbooks-account") {
    return withVariableToggle(
      <QuickBooksAccountSelector
        label={hideLabel ? "" : property.title || formatLabel(name)}
        description={hideLabel ? "" : property.description}
        value={value ?? ""}
        onChange={handleChange}
        required={property.required || false}
        connectorId={connectorId}
        connectorType={connectorType}
      />,
    );
  }

  // Generic connector account selector - handles any connector type (OAuth or form-based)
  if (property.type === "connector-account" || property.format === "connector-account") {
    return (
      <ConnectorAccountSelector
        label={hideLabel ? "" : property.title || formatLabel(name)}
        description={hideLabel ? "" : property.description}
        value={value ?? ""}
        onChange={handleChange}
        connectorType={property.connector_type}
      />
    );
  }

  // Telegram chat selector - searchable dropdown of verified chats
  if (property.type === "telegram-chat" || property.format === "telegram-chat") {
    return (
      <TelegramChatSelector
        label={hideLabel ? "" : property.title || formatLabel(name)}
        description={hideLabel ? "" : property.description}
        value={value ?? ""}
        onChange={handleChange}
        required={property.required || false}
        connectorId={connectorId}
      />
    );
  }

  // Pushover recipient selector - searchable dropdown of registered user keys
  if (property.type === "pushover-recipient" || property.format === "pushover-recipient") {
    return (
      <PushoverRecipientSelector
        label={hideLabel ? "" : property.title || formatLabel(name)}
        description={hideLabel ? "" : property.description}
        value={value ?? ""}
        onChange={handleChange}
        required={property.required || false}
        connectorId={connectorId}
      />
    );
  }

  // Pushbullet device selector - searchable dropdown of registered devices
  if (property.type === "pushbullet-device" || property.format === "pushbullet-device") {
    return (
      <PushbulletDeviceSelector
        label={hideLabel ? "" : property.title || formatLabel(name)}
        description={hideLabel ? "" : property.description}
        value={value ?? ""}
        onChange={handleChange}
        required={property.required || false}
        connectorId={connectorId}
      />
    );
  }

  // Web Push subscription selector with inline "Subscribe this browser" button
  if (property.type === "webpush-subscription" || property.format === "webpush-subscription") {
    return (
      <WebPushSubscriptionSelector
        label={hideLabel ? "" : property.title || formatLabel(name)}
        description={hideLabel ? "" : property.description}
        value={value ?? ""}
        onChange={handleChange}
        required={property.required || false}
        connectorId={connectorId}
      />
    );
  }

  // Turnkey wallet selector
  if (property.type === "turnkey-wallet") {
    // Wallet chain filtering priority:
    // 1. Explicit chainId from form (user selected a chain)
    // 2. chainFamily hint from AI ASK question (Caddie passes "solana" or "evm")
    // 3. Infer from action node type (Solana actions → Solana wallets)
    const chainFamily = (property as Record<string, unknown>).chainFamily;
    const isSolana = chainFamily === "solana" || isSolanaNodeType(currentNodeType ?? "", nodeDefinitions);
    const walletChainId =
      typeof allValues.chainId === "number" ? allValues.chainId : isSolana ? SOLANA_MAINNET_CHAIN_ID : 1; // Default to EVM — filters out Solana wallets for non-Solana actions
    return withVariableToggle(
      <WalletSelectorInput
        label={hideLabel ? "" : property.title || formatLabel(name)}
        description={hideLabel ? "" : property.description}
        value={value ?? ""}
        onChange={handleChange}
        chainId={walletChainId}
      />,
    );
  }

  // Workflow selector for trigger-workflow action
  if (property.format === "workflow-selector") {
    return (
      <WorkflowSelector
        label={hideLabel ? "" : property.title || formatLabel(name)}
        description={hideLabel ? "" : property.description}
        value={value ?? ""}
        onChange={handleChange}
        onFieldChange={onChange}
      />
    );
  }

  // Workflow payload — structured fields from target workflow's inputSchema
  if (property.format === "workflow-payload") {
    return (
      <WorkflowPayloadFields
        label={hideLabel ? "" : property.title || formatLabel(name)}
        description={hideLabel ? "" : property.description}
        value={value ?? {}}
        onChange={handleChange}
        workflowId={allValues?.workflowId as string | undefined}
        currentNodeId={currentNodeId}
        allNodes={allNodes}
        allEdges={allEdges}
        nodeDefinitions={nodeDefinitions}
      />
    );
  }

  // Email-from input with connector-aware default hint
  if (property.format === "email-from") {
    return withVariableToggle(
      <EmailFromInput
        label={hideLabel ? "" : property.title || formatLabel(name)}
        description={hideLabel ? "" : property.description}
        value={value ?? ""}
        onChange={handleChange}
        placeholder={property.placeholder}
        connectorId={connectorId}
        currentNodeId={currentNodeId}
        allNodes={allNodes}
        allEdges={allEdges}
        nodeDefinitions={nodeDefinitions}
      />,
    );
  }

  // Google Sheet URL/ID input with validation and parsing
  if (property.format === "google-sheet-url") {
    return withVariableToggle(
      <GoogleSheetInput
        label={hideLabel ? "" : property.title || formatLabel(name)}
        description={hideLabel ? "" : property.description}
        value={value ?? ""}
        onChange={handleChange}
        required={property.required || false}
        connectorId={connectorId}
        connectorType={connectorType}
      />,
    );
  }

  // Email input with validation
  if (property.type === "email" || (property.format === "email" && property.type === "string")) {
    return withVariableToggle(
      <EmailInput
        label={hideLabel ? "" : property.title || formatLabel(name)}
        description={hideLabel ? "" : property.description}
        value={value ?? property.default ?? ""}
        onChange={handleChange}
        placeholder={property.placeholder}
        required={property.required || false}
      />,
    );
  }

  // Not a connector field — return null to signal "not handled"
  return null;
}
